// The Titles tab opens with a toolbar: a search, All · Games · Apps with live counts, and the Featured and Installed for
// players chips (spec/13 §9.1).
// The venue Library (spec/13 §9.1, F-CAT-02): the titles this venue offers, drawn from the platform
// catalogue (HQ's `game`, F-CAT-01). The list shows each entry's cover, its kiosk category, the
// station classes it appears on (empty means every class), whether it is featured and visible, and its
// launch-recipe count. "Add title" is a two-step Stepper in a centred dialog (Pick a title → Offer it, BRAND.md
// §6.1); an entry opens an edit dialog or is removed. Managing the library needs `library:manage`; a
// member with only `library:read` sees it read-only.
//
// **Apps** (spec/14 §6): the catalogue holds HQ-added apps beside the games. The pick step filters All ·
// Games · Apps, an app's square icon is its cover, and adding an app defaults its kiosk category to the one
// the app suggests (a browser under Browsers, Discord under Voice…) — the venue can still change it.
// Install detection (F-CAT-03): below the titles, "Detected on your PCs, not in your library" lists what the
// stations found that no entry matches, with one-click add where the catalogue holds it; a second tab, "On your
// PCs", is the titles × stations matrix with "Rescan all" (`stations:manage`).
//
// **Custom apps** (spec/13 §9): the organisation's own apps, not in the catalogue (`CustomApps.tsx`). "Add custom
// app" opens a short stepper; the "Custom apps" tab lists them with edit, archive and "Add to this venue"; and the
// Add title picker shows them beside the catalogue's apps with a "Custom" badge. An entry offers either a catalogue
// title or a custom app.
//
// **Install for players** (F-CAT-09, ADR-0045): an app HQ gave a provisioning recipe (Discord, Spotify, WhatsApp — apps
// that install per Windows account) has a one-line status on its card (On all 3 PCs, On 2 of 3 PCs, Install failed on 1 PC); the
// switch is in the entry's edit dialog, opened from the card's menu ("Install for players…"): on, the venue's PCs install
// it into the account players use while no one is playing. Under it, from the same matrix read as "On your PCs", how many
// PCs have it for players and what is in the way — or, off, that some PCs have it for another Windows account only, which
// players cannot open. Off by default; `library:manage` turns it on or off (a read-only member sees it, disabled). Its
// card menu also offers "Install on PCs missing it" (ADR-0056, `InstallOnPcs.tsx`): a one-off install on every PC that
// does not have it, as soon as each is free, after a centred confirm.

import type {
  CatalogueKind,
  CustomApp,
  Game,
  LibraryCategory,
  LibraryEntry,
  LibraryInstallMatrix,
  UpdateLibraryEntry,
  VenueCatalogueGame,
  VenueCustomApp,
} from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Chip, Dialog, EmptyState, SegmentedControl, SelectField, Skeleton, Stepper, Switch, Tabs, TextField, useToast } from '@clavian/ui-web';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useAccess } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { useStationClasses } from '../../stations/queries';
import { CardsSkeleton, Forbidden, Notice, Page, RowActions, SearchField } from '../../ui';
import { CoverArt } from '../product-art';
import { CustomAppDialog, CustomAppIcon, CustomAppsPanel } from './CustomApps';
import { DetectedTitles } from './DetectedTitles';
import { InstallMatrix } from './InstallMatrix';
import { statusOfState } from '../../floor/station-apps';
import { InstallAppButton } from './InstallAppButton';
import { InstallOnPcsDialog } from './InstallOnPcs';
import { playersNote, playersStations, playersStatus, playersSummary } from './players';
import { useAddLibraryEntry, useBrowseCatalogue, useInstallMatrix, useLibraryEntries, useRemoveLibraryEntry, useUpdateLibraryEntry, useVenueCustomApps } from './queries';

import './library.css';
import { useProblem } from '../../api/use-problem';

const LIBRARY_MANAGE = 'library:manage';
const STATIONS_MANAGE = 'stations:manage';

/** The kiosk categories a title is grouped under (spec/13 §9.1). Order is the picker's order. */
const CATEGORIES: readonly LibraryCategory[] = ['games', 'competitive', 'casual', 'apps', 'browsers', 'voice', 'media', 'tools'];

/**
 * The category a newly added title starts under: a game under Games; an app under the category it suggests
 * (spec/14 §6), or Apps when it suggests none the library knows.
 */
export function defaultCategoryFor(game: Pick<Game, 'kind' | 'suggestedCategory'>): LibraryCategory {
  if (game.kind !== 'app') return 'games';
  const suggested = game.suggestedCategory;
  return suggested !== null && (CATEGORIES as readonly string[]).includes(suggested) ? (suggested as LibraryCategory) : 'apps';
}

/** The cover to draw: our own CDN copy where cached, else the IGDB reference, else a placeholder. */
function coverSrc(game: Pick<Game, 'coverUrl' | 'coverImageId'>): string | null {
  if (game.coverUrl !== null && game.coverUrl !== '') return game.coverUrl;
  return game.coverImageId !== null && game.coverImageId !== '' ? `https://images.igdb.com/igdb/image/upload/t_cover_big/${game.coverImageId}.jpg` : null;
}

/** What an entry offers, as the list draws it: a catalogue title or one of the organisation's custom apps. */
export interface EntryTitle {
  readonly name: string;
  readonly cover: string | null;
  readonly app: boolean;
  readonly custom: boolean;
  readonly archived: boolean;
  readonly recipes: number;
}

export function entryTitle(entry: LibraryEntry): EntryTitle {
  if (entry.game !== null) {
    return {
      name: entry.game.name,
      cover: coverSrc(entry.game),
      app: entry.game.kind === 'app',
      custom: false,
      // HQ archived the catalogue title (F-CAT-01): the entry keeps working, venues just cannot add it anew.
      archived: entry.game.archivedAt !== null,
      recipes: entry.game.launchRecipes.length,
    };
  }
  const app = entry.customApp;
  return {
    name: app?.name ?? '',
    cover: app?.iconUrl ?? null,
    app: true,
    custom: true,
    archived: app !== null && app.archivedAt !== null,
    recipes: app?.launchRecipes.length ?? 0,
  };
}

/** The Titles toolbar's kind filter: Games are catalogue games; Apps are catalogue apps and the organisation's custom apps. */
type KindFilter = 'all' | 'games' | 'apps';

const isApp = (entry: LibraryEntry): boolean => entryTitle(entry).app;

const matchesTitle = (entry: LibraryEntry, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  return needle === '' || entryTitle(entry).name.toLowerCase().includes(needle);
};

/** What the Add title picker picked: a catalogue title, or one of the organisation's custom apps. */
type Picked = { readonly kind: 'catalogue'; readonly game: Game } | { readonly kind: 'custom'; readonly app: CustomApp };

const pickedName = (picked: Picked) => (picked.kind === 'catalogue' ? picked.game.name : picked.app.name);

export function LibraryPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const organizationId = venue.organizationId;
  const access = useAccess({ venueId: venue.id });
  const canManage = access.status === 'ready' && access.can(LIBRARY_MANAGE);
  const canRescan = access.status === 'ready' && access.can(STATIONS_MANAGE);

  const entries = useLibraryEntries(organizationId, venue.id);
  const classes = useStationClasses(organizationId, venue.id);
  const [adding, setAdding] = useState(false);
  // The entry being edited, by id so the dialog reads the live row (its Install for players switch saves at once); `focus`
  // says which section the dialog opens on.
  const [editing, setEditing] = useState<{ readonly id: string; readonly focus: 'players' | undefined } | null>(null);
  // The Titles toolbar: a search, All · Games · Apps, and two chips that narrow further (client side, over the loaded rows).
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [installedOnly, setInstalledOnly] = useState(false);
  // The custom app dialog: `undefined` closed, `null` creating, an app editing.
  const [customApp, setCustomApp] = useState<CustomApp | null | undefined>(undefined);
  // The matrix says where each app stands on the PCs; read only when some entry can be installed for players.
  const provisionable = (entries.data?.pages ?? []).some((page) => page.data.some((entry) => (entry.game?.provisioning ?? null) !== null));
  const matrix = useInstallMatrix(organizationId, venue.id, provisionable);

  // A read denied by role is the honest answer, not a spinner that never resolves.
  if (access.status === 'ready' && !access.can('library:read')) return <Forbidden message={t('library.forbidden')} />;

  const classNameById = new Map((classes.data ?? []).map((klass) => [klass.id, klass.name]));
  // With no title yet the empty state offers Add title; the header does not offer it a second time.
  const titlesEmpty = entries.data !== undefined && entries.data.pages.every((page) => page.data.length === 0);

  let body: ReactNode;
  if (entries.data === undefined) {
    body = entries.isError ? (
      <LoadFailure error={entries.error} message={t('library.loadFailed')} onRetry={() => void entries.refetch()} />
    ) : (
      <CardsSkeleton label={t('library.loading')} />
    );
  } else {
    const rows = entries.data.pages.flatMap((page) => page.data);
    const narrowed = rows.filter((entry) => matchesTitle(entry, query) && (!featuredOnly || entry.featured) && (!installedOnly || entry.installForPlayers));
    const counts = { all: narrowed.length, games: narrowed.filter((entry) => !isApp(entry)).length, apps: narrowed.filter(isApp).length };
    const shown = kind === 'all' ? narrowed : narrowed.filter((entry) => isApp(entry) === (kind === 'apps'));
    const filtering = query.trim() !== '' || kind !== 'all' || featuredOnly || installedOnly;
    const clearFilters = () => {
      setQuery('');
      setKind('all');
      setFeaturedOnly(false);
      setInstalledOnly(false);
    };
    body =
      rows.length === 0 ? (
        <EmptyState
          icon={<NavIcon name="library" />}
          title={t('library.empty.title')}
          description={t('library.empty.body')}
          action={canManage ? <Button variant="primary" onClick={() => setAdding(true)}>{t('library.add.open')}</Button> : undefined}
        />
      ) : (
        <>
          <div className="console-library-toolbar">
            <SearchField className="console-library-toolbar__search" label={t('library.filter.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
            <SegmentedControl<KindFilter>
              size="sm"
              label={t('library.filter.kind')}
              value={kind}
              onValueChange={setKind}
              options={[
                { value: 'all', label: t('library.filter.all'), count: counts.all },
                { value: 'games', label: t('library.filter.games'), count: counts.games },
                { value: 'apps', label: t('library.filter.apps'), count: counts.apps },
              ]}
            />
            <div className="console-library-toolbar__toggles">
              <Chip icon={<Icon name="star" />} selected={featuredOnly} onSelectedChange={setFeaturedOnly}>
                {t('library.filter.featured')}
              </Chip>
              <Chip icon={<Icon name="download" />} selected={installedOnly} onSelectedChange={setInstalledOnly}>
                {t('library.filter.installed')}
              </Chip>
            </div>
          </div>
          {shown.length === 0 ? (
            <EmptyState
              icon={<NavIcon name="library" />}
              title={t('library.filter.emptyTitle')}
              description={t('library.filter.emptyBody')}
              action={filtering ? <Button onClick={clearFilters}>{t('library.filter.clear')}</Button> : undefined}
            />
          ) : (
            <ul className="console-library-grid" aria-label={t('library.list.caption')}>
              {shown.map((entry) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  classNames={entry.classIds.flatMap((id) => classNameById.get(id) ?? [])}
                  classCount={entry.classIds.length}
                  canManage={canManage}
                  onEdit={() => setEditing({ id: entry.id, focus: undefined })}
                  onInstallForPlayers={() => setEditing({ id: entry.id, focus: 'players' })}
                  organizationId={organizationId}
                  venueId={venue.id}
                  matrix={matrix.data}
                />
              ))}
            </ul>
          )}
          {entries.hasNextPage ? (
            <div className="console-library-more">
              <Button busy={entries.isFetchingNextPage} onClick={() => void entries.fetchNextPage()}>
                {t('library.list.more')}
              </Button>
            </div>
          ) : null}
        </>
      );
  }

  const editingEntry = editing === null ? undefined : (entries.data?.pages ?? []).flatMap((page) => page.data).find((entry) => entry.id === editing.id);

  return (
    <Page
      title={t('library.title')}
      description={t('library.intro')}
      // One primary action for the page: Add title. Add custom app lives on the Custom apps tab, beside the apps it adds to.
      actions={
        canManage && !titlesEmpty ? (
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setAdding(true)}>
            {t('library.add.open')}
          </Button>
        ) : undefined
      }
    >
      {access.status === 'ready' && !canManage ? <Notice role="note">{t('library.readOnly')}</Notice> : null}
      <Tabs
        className="console-library-tabs"
        label={t('library.tabs')}
        items={[
          {
            value: 'titles',
            label: t('library.tab.titles'),
            content: (
              <>
                {body}
                <DetectedTitles organizationId={organizationId} venueId={venue.id} canManage={canManage} />
              </>
            ),
          },
          {
            value: 'custom-apps',
            label: t('library.tab.customApps'),
            content: <CustomAppsPanel organizationId={organizationId} venueId={venue.id} canManage={canManage} onCreate={() => setCustomApp(null)} onEdit={(app) => setCustomApp(app)} />,
          },
          { value: 'installs', label: t('library.tab.installs'), content: <InstallMatrix organizationId={organizationId} venueId={venue.id} canRescan={canRescan} /> },
        ]}
      />
      {adding ? <AddTitleDialog organizationId={organizationId} venueId={venue.id} open onOpenChange={setAdding} /> : null}
      {customApp !== undefined ? (
        <CustomAppDialog organizationId={organizationId} venueId={venue.id} app={customApp ?? undefined} open onOpenChange={(open) => !open && setCustomApp(undefined)} />
      ) : null}
      {editing !== null && editingEntry !== undefined ? (
        <EditEntryDialog
          organizationId={organizationId}
          venueId={venue.id}
          entry={editingEntry}
          matrix={matrix.data}
          focus={editing.focus}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      ) : null}
    </Page>
  );
}

// -------------------------------------------------------------------------------------------------
// One entry
// -------------------------------------------------------------------------------------------------

function EntryCard({
  entry,
  classNames,
  classCount,
  canManage,
  onEdit,
  onInstallForPlayers,
  organizationId,
  venueId,
  matrix,
}: {
  readonly entry: LibraryEntry;
  readonly classNames: readonly string[];
  /** How many classes it is limited to (0 is every class); their names may not all be readable here. */
  readonly classCount: number;
  readonly canManage: boolean;
  readonly onEdit: () => void;
  /** Opens the edit dialog on its Install for players section. */
  readonly onInstallForPlayers: () => void;
  readonly organizationId: string;
  readonly venueId: string;
  /** The install matrix, for the status line; undefined while it loads (or when no entry needs it). */
  readonly matrix: LibraryInstallMatrix | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const remove = useRemoveLibraryEntry(organizationId, venueId);
  const title = entryTitle(entry);
  const src = title.cover;
  const recipes = title.recipes;
  // ADR-0056: an app with a provisioning recipe can be installed now on every PC missing it, after a centred confirm.
  const provisionable = (entry.game?.provisioning ?? null) !== null;
  const [installing, setInstalling] = useState(false);
  const classes = classCount === 0 ? t('library.card.allPcs') : classNames.length === 0 ? t('library.classes.count', { count: classCount }) : classNames.join(', ');
  const meta = `${classes} · ${recipes === 0 ? t('library.launch.none') : t('library.launch.count', { count: recipes })}`;

  return (
    <li className="console-library-card">
      {/* The cover, or — with none, or one that will not load — its initial on a plate of its own tint, as Sell's
          dishes do (ADR-0043): never the browser's broken-image glyph. */}
      <CoverArt id={entry.id} name={title.name} src={src} className="console-library-cover" />
      <div className="console-library-card__body">
        <p className="console-library-card__title">
          <span className="console-library-card__name" title={title.name}>{title.name}</span>
          {entry.featured ? (
            <span className="console-library-card__star" role="img" aria-label={t('library.card.featuredLabel')}>
              <Icon name="star" />
            </span>
          ) : null}
        </p>
        <p className="console-library-card__meta">
          <Badge>{t(`library.category.${entry.category}`)}</Badge>
          {title.archived ? <Badge tone="warn">{t('library.badge.archived')}</Badge> : null}
          {!entry.visible ? <Badge tone="warn">{t('library.badge.hidden')}</Badge> : null}
        </p>
        <p className="console-library-card__classes" title={meta}>{meta}</p>
        <PlayersStatusLine entry={entry} provisionable={provisionable} matrix={matrix} />
      </div>
      {canManage ? (
        <RowActions
          name={title.name}
          items={[
            { id: 'edit', label: t('common.edit'), onSelect: onEdit },
            ...(provisionable ? [{ id: 'install-for-players', label: t('library.card.menuInstallForPlayers'), onSelect: onInstallForPlayers }] : []),
            ...(provisionable ? [{ id: 'install', label: t('library.install.menu'), onSelect: () => setInstalling(true) }] : []),
            {
              id: 'remove',
              label: t('common.remove'),
              tone: 'danger',
              onSelect: () =>
                remove.mutate(
                  { entryId: entry.id },
                  {
                    onSuccess: () => show({ tone: 'ok', title: t('library.remove.done', { game: title.name }) }),
                    onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('library.remove.failed') }) }),
                  },
                ),
            },
          ]}
        />
      ) : null}
      {installing ? <InstallOnPcsDialog organizationId={organizationId} venueId={venueId} entry={entry} name={title.name} onClose={() => setInstalling(false)} /> : null}
    </li>
  );
}

/**
 * The card's one line on Install for players (F-CAT-09): muted when off, else a coloured dot and where the app stands on the
 * PCs. Always one line tall (empty for an entry that cannot be installed for players) so cards in a row are equal height.
 */
function PlayersStatusLine({ entry, provisionable, matrix }: { readonly entry: LibraryEntry; readonly provisionable: boolean; readonly matrix: LibraryInstallMatrix | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const blank = <p className="console-library-card__status" aria-hidden="true" />;
  if (!provisionable) return blank;
  // Off needs no matrix; on waits for it rather than guessing.
  if (matrix === undefined && entry.installForPlayers) return blank;
  const status = entry.installForPlayers && matrix !== undefined ? playersStatus(playersSummary(matrix, entry.id), true) : { key: 'off' as const, tone: 'muted' as const, installed: 0, total: 0 };
  const text =
    status.key === 'off'
      ? t('library.card.status.off')
      : status.key === 'notReported'
        ? t('library.card.status.notReported')
        : status.key === 'all'
          ? t('library.card.status.all', { count: status.total })
          : status.key === 'queued'
            ? t('library.card.status.queued', { count: status.total })
          : status.key === 'some'
            ? t('library.card.status.some', { n: status.installed, count: status.total })
            : t('library.card.status.failed', { count: status.total });
  return (
    <p className={`console-library-card__status is-${status.tone}`}>
      {status.tone === 'muted' ? null : <span className="console-library-card__dot" aria-hidden="true" />}
      <span>{text}</span>
    </p>
  );
}

/**
 * The edit dialog's Install for players section (F-CAT-09, ADR-0045): the switch, one line on what it does, where the app
 * stands on the PCs, and each PC's own state. The switch saves at once (an audited edit of the entry) and says so. Opened
 * from the card's menu it scrolls into view and takes focus.
 */
function InstallForPlayers({
  entry,
  name,
  canManage,
  organizationId,
  venueId,
  matrix,
  autoFocus = false,
}: {
  readonly autoFocus?: boolean;
  readonly entry: LibraryEntry;
  readonly name: string;
  readonly canManage: boolean;
  readonly organizationId: string;
  readonly venueId: string;
  readonly matrix: LibraryInstallMatrix | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const update = useUpdateLibraryEntry(organizationId, venueId);
  const hintId = `console-players-hint-${entry.id}`;
  const summary = matrix === undefined ? undefined : playersSummary(matrix, entry.id);
  const note = summary === undefined ? null : playersNote(summary, entry.installForPlayers);
  const stations = matrix === undefined ? [] : playersStations(matrix, entry.id);
  const sectionRef = useRef<HTMLDivElement>(null);

  // After the dialog has taken its own initial focus, move to the switch and bring the section into view.
  useEffect(() => {
    if (!autoFocus) return;
    const timer = window.setTimeout(() => {
      const section = sectionRef.current;
      if (section === null) return;
      if (typeof section.scrollIntoView === 'function') section.scrollIntoView({ block: 'center' });
      section.querySelector<HTMLElement>('[role="switch"]')?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);

  const toggle = (on: boolean) =>
    update.mutate(
      { entryId: entry.id, body: { installForPlayers: on } },
      {
        onSuccess: () => show({ tone: 'ok', title: on ? t('library.players.on', { app: name }) : t('library.players.off', { app: name }) }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('library.players.failed') }) }),
      },
    );

  return (
    <div className="console-library-players" role="group" aria-label={t('library.players.section')} ref={sectionRef}>
      <Switch
        label={t('library.players.label')}
        stateLabel
        checked={entry.installForPlayers}
        disabled={!canManage || update.isPending}
        aria-describedby={hintId}
        onCheckedChange={toggle}
      />
      <p id={hintId} className="console-library-players__hint">
        {t('library.players.hint')}
      </p>
      {matrix === undefined ? null : (
        <>
          <p className="console-library-players__heading">{t('library.players.pcs')}</p>
          {stations.length === 0 ? (
            <p className="console-library-players__hint">{t('library.players.pcsEmpty')}</p>
          ) : (
            <ul className="console-library-players__pcs" aria-label={t('library.players.pcs')}>
              {stations.map((station) => {
                const status = statusOfState(station.state);
                return (
                  <li key={station.stationId} className="console-library-players__pc">
                    <span className="console-num">{station.name ?? t('floor.tile.numbered', { number: station.number })}</span>
                    <span className="console-library-players__pc-end">
                      <Badge tone={status.tone}>{t(`library.players.pill.${status.key}`)}</Badge>
                      {canManage && station.action !== null ? (
                        <InstallAppButton stationId={station.stationId} entryId={entry.id} name={name} action={station.action} />
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {note === null ? null : <p className="console-library-players__hint">{t(note)}</p>}
        </>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Add a title: Pick → Offer
// -------------------------------------------------------------------------------------------------

function AddTitleDialog({
  organizationId,
  venueId,
  open,
  onOpenChange,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" title={t('library.add.title')} description={t('library.add.intro')}>
      {open ? <AddTitleFlow organizationId={organizationId} venueId={venueId} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function AddTitleFlow({ organizationId, venueId, onDone }: { readonly organizationId: string; readonly venueId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState<Picked | null>(null);

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={picked === null ? undefined : setStep}
        steps={[
          { id: 'pick', label: t('library.add.stepPick'), ...(picked === null ? {} : { description: pickedName(picked) }) },
          { id: 'offer', label: t('library.add.stepOffer') },
        ]}
      />
      {step === 0 ? (
        <PickTitleStep
          organizationId={organizationId}
          venueId={venueId}
          onPick={(title) => {
            setPicked(title);
            setStep(1);
          }}
          onCancel={onDone}
        />
      ) : picked !== null ? (
        <OfferStep organizationId={organizationId} venueId={venueId} picked={picked} onBack={() => setStep(0)} onDone={onDone} />
      ) : null}
    </div>
  );
}

function PickTitleStep({
  organizationId,
  venueId,
  onPick,
  onCancel,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly onPick: (title: Picked) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<CatalogueKind | 'all'>('all');
  const browse = useBrowseCatalogue(organizationId, venueId, q, kind === 'all' ? undefined : kind);
  const rows = browse.data?.pages.flatMap((page) => page.data) ?? [];
  // The organisation's custom apps sit beside the catalogue's apps (spec/13 §9), filtered by the same name search.
  const custom = useVenueCustomApps(organizationId, venueId, kind !== 'game');
  const needle = q.toLowerCase();
  const customRows =
    kind === 'game' ? [] : (custom.data?.pages.flatMap((page) => page.data) ?? []).filter((row) => needle === '' || row.customApp.name.toLowerCase().includes(needle));

  return (
    <div className="console-form">
      <form
        role="search"
        className="console-library-search"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(text.trim());
        }}
      >
        <SearchField label={t('library.add.searchLabel')} placeholder={t('library.add.searchPlaceholder')} value={text} onChange={(event) => setText(event.target.value)} />
        <Button type="submit" variant="secondary">{t('common.search')}</Button>
      </form>
      <SegmentedControl<CatalogueKind | 'all'>
        label={t('library.add.filter')}
        size="sm"
        options={[
          { value: 'all', label: t('library.add.filterAll') },
          { value: 'game', label: t('library.add.filterGames') },
          { value: 'app', label: t('library.add.filterApps') },
        ]}
        value={kind}
        onValueChange={setKind}
      />

      {browse.data === undefined ? (
        browse.isError ? (
          <LoadFailure error={browse.error} message={t('library.add.browseFailed')} onRetry={() => void browse.refetch()} />
        ) : (
          <CardsSkeleton count={8} height="12rem" label={t('library.add.loading')} />
        )
      ) : rows.length === 0 && customRows.length === 0 ? (
        <p className="console-muted" role="status">{t('library.add.noResults')}</p>
      ) : (
        <>
          <ul className="console-library-pick" aria-label={t('library.add.results')}>
            {customRows.map((row) => (
              <CustomPickCard key={row.customApp.id} row={row} onPick={() => onPick({ kind: 'custom', app: row.customApp })} />
            ))}
            {rows.map((candidate) => (
              <PickCard key={candidate.game.id} candidate={candidate} onPick={() => onPick({ kind: 'catalogue', game: candidate.game })} />
            ))}
          </ul>
          {browse.hasNextPage ? (
            <div className="console-library-more">
              <Button busy={browse.isFetchingNextPage} onClick={() => void browse.fetchNextPage()}>
                {t('library.list.more')}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <div className="console-form__actions console-form__actions--end">
        <Button variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </div>
  );
}

function PickCard({ candidate, onPick }: { readonly candidate: VenueCatalogueGame; readonly onPick: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const src = coverSrc(candidate.game);
  return (
    <li className="console-library-pick__card">
      <CoverArt id={candidate.game.id} name={candidate.game.name} src={src} className="console-library-cover" />
      <p className="console-library-pick__name">{candidate.game.name}</p>
      {candidate.inLibrary ? (
        <Badge tone="ok">{t('library.add.inLibrary')}</Badge>
      ) : (
        <Button size="sm" variant="secondary" onClick={onPick}>{t('library.add.pick')}</Button>
      )}
    </li>
  );
}

/** One of the organisation's custom apps in the picker, badged "Custom". */
function CustomPickCard({ row, onPick }: { readonly row: VenueCustomApp; readonly onPick: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <li className="console-library-pick__card">
      <CustomAppIcon app={row.customApp} size={120} />
      <p className="console-library-pick__name">{row.customApp.name}</p>
      <Badge>{t('library.badge.custom')}</Badge>
      {row.inLibrary ? (
        <Badge tone="ok">{t('library.add.inLibrary')}</Badge>
      ) : (
        <Button size="sm" variant="secondary" onClick={onPick}>{t('library.add.pick')}</Button>
      )}
    </li>
  );
}

function OfferStep({
  organizationId,
  venueId,
  picked,
  onBack,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly picked: Picked;
  readonly onBack: () => void;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const add = useAddLibraryEntry(organizationId, venueId);
  const name = pickedName(picked);
  const [offer, setOffer] = useState<OfferState>({
    category: picked.kind === 'catalogue' ? defaultCategoryFor(picked.game) : picked.app.suggestedCategory,
    classIds: [],
    featured: false,
    visible: true,
  });
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <form
      className="console-form"
      aria-label={t('library.add.offerFor', { game: name })}
      onSubmit={(event) => {
        event.preventDefault();
        add.mutate(
          {
            ...(picked.kind === 'catalogue' ? { gameId: picked.game.id } : { customAppId: picked.app.id }),
            category: offer.category,
            classIds: offer.classIds,
            featured: offer.featured,
            visible: offer.visible,
          },
          {
            onSuccess: () => {
              show({ tone: 'ok', title: t('library.add.added', { game: name }) });
              onDone();
            },
            onError: (err) => setError(problemCode(err) === PROBLEM.conflict ? t('library.add.already') : problem.text(err, { fallback: t('library.add.failed') })),
          },
        );
      }}
    >
      <p className="console-library-offer__game">{name}</p>
      {error !== undefined ? <Notice tone="danger" role="alert">{error}</Notice> : null}
      <OfferFields organizationId={organizationId} venueId={venueId} value={offer} onChange={setOffer} />
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onBack}>{t('common.back')}</Button>
        <Button type="submit" variant="primary" busy={add.isPending}>{t('library.add.submit')}</Button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------------------------------------
// Edit an entry
// -------------------------------------------------------------------------------------------------

function EditEntryDialog({
  organizationId,
  venueId,
  entry,
  matrix,
  focus,
  open,
  onOpenChange,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly entry: LibraryEntry;
  /** The install matrix, for the Install for players section. */
  readonly matrix: LibraryInstallMatrix | undefined;
  /** `players` opens the dialog scrolled to, and focused on, the Install for players switch. */
  readonly focus: 'players' | undefined;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const update = useUpdateLibraryEntry(organizationId, venueId);
  const [offer, setOffer] = useState<OfferState>({ category: entry.category, classIds: [...entry.classIds], featured: entry.featured, visible: entry.visible });
  const [sortOrder, setSortOrder] = useState(String(entry.sortOrder));
  // spec/11 §3.5: only a title HQ flags as needing admin rights has the override; the switch reads "launch as admin".
  const [elevated, setElevated] = useState(!entry.elevationDisabled);
  const elevatable = entry.game?.requiresElevation === true;
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md" dirty="auto" title={t('library.edit.title')} description={entryTitle(entry).name}>
      <form
        className="console-form"
        aria-label={t('library.edit.title')}
        onSubmit={(event) => {
          event.preventDefault();
          const parsed = Number(sortOrder);
          const order = sortOrder.trim() === '' || !Number.isInteger(parsed) || parsed < 0 ? 0 : parsed;
          const body: UpdateLibraryEntry = {
            category: offer.category,
            classIds: offer.classIds,
            featured: offer.featured,
            visible: offer.visible,
            sortOrder: order,
            ...(elevatable && elevated === entry.elevationDisabled ? { elevationDisabled: !elevated } : {}),
          };
          update.mutate(
            { entryId: entry.id, body },
            {
              onSuccess: () => {
                show({ tone: 'ok', title: t('library.edit.saved', { game: entryTitle(entry).name }) });
                onOpenChange(false);
              },
              onError: (failure) => setError(problem.text(failure, { fallback: t('library.edit.failed') })),
            },
          );
        }}
      >
        {error !== undefined ? <Notice tone="danger" role="alert">{error}</Notice> : null}
        <OfferFields organizationId={organizationId} venueId={venueId} value={offer} onChange={setOffer} />
        <TextField label={t('library.edit.sortOrder')} description={t('library.edit.sortOrderHint')} numeric inputMode="numeric" value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} />
        {elevatable ? (
          <div className="console-library-toggle">
            <Switch label={t('library.field.elevation')} stateLabel checked={elevated} onCheckedChange={setElevated} />
            <p className="console-muted">{t('library.field.elevationHint')}</p>
          </div>
        ) : null}
        {(entry.game?.provisioning ?? null) !== null ? (
          <InstallForPlayers entry={entry} name={entryTitle(entry).name} canManage organizationId={organizationId} venueId={venueId} matrix={matrix} autoFocus={focus === 'players'} />
        ) : null}
        <div className="console-form__actions console-form__actions--end">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" busy={update.isPending}>{t('library.edit.submit')}</Button>
        </div>
      </form>
    </Dialog>
  );
}

// -------------------------------------------------------------------------------------------------
// The shared offer controls: category, classes, featured, visible
// -------------------------------------------------------------------------------------------------

interface OfferState {
  readonly category: LibraryCategory;
  readonly classIds: string[];
  readonly featured: boolean;
  readonly visible: boolean;
}

function OfferFields({
  organizationId,
  venueId,
  value,
  onChange,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly value: OfferState;
  readonly onChange: (value: OfferState) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const classes = useStationClasses(organizationId, venueId);
  const options = useMemo(() => CATEGORIES.map((category) => ({ value: category, label: t(`library.category.${category}`) })), [t]);
  const classList = classes.data ?? [];

  const toggleClass = (id: string) => {
    const next = value.classIds.includes(id) ? value.classIds.filter((candidate) => candidate !== id) : [...value.classIds, id];
    onChange({ ...value, classIds: next });
  };

  return (
    <>
      <SelectField label={t('library.field.category')} options={options} value={value.category} onValueChange={(category) => onChange({ ...value, category: category as LibraryCategory })} />

      <fieldset className="console-library-classes">
        <legend className="clav-field__label">{t('library.field.classes')}</legend>
        <p className="console-muted">{t('library.field.classesHint')}</p>
        {classList.length === 0 ? (
          <p className="console-muted">{t('library.field.noClasses')}</p>
        ) : (
          <div className="console-library-classes__grid">
            {classList.map((klass) => (
              <Checkbox key={klass.id} label={klass.name} checked={value.classIds.includes(klass.id)} onCheckedChange={() => toggleClass(klass.id)} />
            ))}
          </div>
        )}
      </fieldset>

      <div className="console-library-toggle">
        <Switch label={t('library.field.featured')} stateLabel checked={value.featured} onCheckedChange={(featured) => onChange({ ...value, featured })} />
        <p className="console-muted">{t('library.field.featuredHint')}</p>
      </div>
      <div className="console-library-toggle">
        <Switch label={t('library.field.visible')} stateLabel checked={value.visible} onCheckedChange={(visible) => onChange({ ...value, visible })} />
        <p className="console-muted">{t('library.field.visibleHint')}</p>
      </div>
    </>
  );
}

/** A quiet skeleton while the page is not yet known — used by the router's pending state. */
export function LibraryLoading(): ReactNode {
  return <Skeleton height="12rem" />;
}
